import { useGetVisualizacaoContaReceber } from "hooks/contaReceber/useGetVisualizacaoContaReceber";
import { ReactNode, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import VisualizacaoInput from "shared/components/input/VisualizacaoInput";
import VisualizacaoTextArea from "shared/components/input/VisualizacaoTextArea";
import CustomModal from "shared/components/modal/CustomModal";
import {
  formatarDataString,
  formatarDataStringParaMesCompetencia,
  formatarValorMonetario,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { VisualizacaoContaReceberClassePlanoDTO } from "types/dto/VisualizacaoContaReceberClassePlanoDTO";
import { VisualizacaoContaReceberDTO } from "types/dto/VisualizacaoContaReceberDTO";

interface ModalVisualizarContaReceberProps {
  showModal: boolean;
  closeModal: () => void;
  contaReceberId: number;
}

const ModalVisualizarContaReceber: React.FC<ModalVisualizarContaReceberProps> = (props) => {
  const [verMaisClasses, setVerMaisClasses] = useState<boolean>(false);
  const [possuiClassePlanoComContaProvisionamento, setPossuiClassePlanoComContaProvisionamento] =
    useState<boolean>(false);
  const { data } = useGetVisualizacaoContaReceber({ contaReceberId: props.contaReceberId });

  useEffect(() => {
    const possuiClassePlanoComContaProvisionamento = (
      data as VisualizacaoContaReceberDTO
    )?.classePlanoVisualizacao?.some((classePlano) => classePlano.nomeContaProvisionamento !== null);

    setPossuiClassePlanoComContaProvisionamento(possuiClassePlanoComContaProvisionamento);
  }, [data]);

  const renderBodyModalVisualizarCobranca = () => {
    let contaReceber = data as VisualizacaoContaReceberDTO;
    if (contaReceber) {
      return (
        <Container>
          <Row>
            <Col sm={6} md={6} lg={6} xl={6}>
              <VisualizacaoInput
                id="mes-competencia"
                label="Mês competência"
                value={formatarDataStringParaMesCompetencia(contaReceber.mesCompetencia)}
              />
            </Col>
            <Col sm={6} md={6} lg={6} xl={6}>
              <VisualizacaoInput
                id="origem-lancamento"
                label="Origem de lançamento"
                value={contaReceber.origemLancamento}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col sm={6} md={6} lg={6} xl={6}>
              <VisualizacaoInput id="unidade" label="Unidade" value={contaReceber.blocoNumeroUnidade} />
            </Col>
            <Col sm={6} md={6} lg={6} xl={6}>
              <VisualizacaoInput id="sacado" label="Sacado" value={contaReceber.nomeSacado} />
            </Col>
          </Row>

          <Row className="mt-3 mb-3">
            <Col sm={6} md={6} lg={6} xl={6}>
              <VisualizacaoInput
                id="data-vencimento"
                label="Data de vencimento"
                value={formatarDataString(contaReceber.dataVencimento)}
              />
            </Col>
            <Col sm={6} md={6} lg={6} xl={6}>
              <VisualizacaoInput id="nosso-numero" label="Nosso número" value={contaReceber.nossoNumero || "-"} />
            </Col>
          </Row>

          {renderClassesPlanoContas(contaReceber.classePlanoVisualizacao)}

          <Row className="mt-3">
            <Col sm={12} md={12} lg={12} xl={12}>
              <VisualizacaoTextArea
                direcaoExpansao="vertical"
                rows={4}
                id="observacao"
                label="Observação"
                value={contaReceber.observacao || "-"}
              />
            </Col>
          </Row>
        </Container>
      );
    }
  };

  const renderClassesPlanoContas = (classesContas: VisualizacaoContaReceberClassePlanoDTO[]) => {
    if (classesContas.length > 0) {
      return classesContas.map((classePlano, index) => {
        const isUltimaClasse = index === classesContas.length - 1;

        if (index <= 2 || verMaisClasses) {
          return (
            <>
              {renderClassePlanoContas(classePlano, index)}
              {verMaisClasses && isUltimaClasse && renderVerMenosClasses()}
            </>
          );
        } else if (index === 3 && !verMaisClasses) {
          return renderVerMaisClasses();
        }
      });
    }
  };

  const renderVerMaisClasses = (): ReactNode => {
    return (
      <div>
        <div className="mt-2 cursor-pointer fit-content" onClick={() => setVerMaisClasses(true)}>
          <span className="sub-titulo"> + Ver mais classes</span>
        </div>
      </div>
    );
  };

  const renderVerMenosClasses = () => {
    return (
      <div>
        <div className="mt-2 cursor-pointer fit-content" onClick={() => setVerMaisClasses(false)}>
          <span className="sub-titulo"> - Ver menos classes</span>
        </div>
      </div>
    );
  };

  const renderClassePlanoContas = (classePlano: VisualizacaoContaReceberClassePlanoDTO, index: number) => {
    const size = possuiClassePlanoComContaProvisionamento ? 4 : 6;
    return (
      <Row>
        <Col className="mt-3" sm={size} md={size} lg={size} xl={size} key={`classe-plano-${index}`}>
          <VisualizacaoInput
            id={`classe-plano-descricao-${index}`}
            label={index === 0 ? "Classe de conta (Categoria)" : ""}
            value={classePlano.descricao}
          />
        </Col>

        <Col className="mt-3" sm={size} md={size} lg={size} xl={size} key={`classe-plano-valor-${index}`}>
          <VisualizacaoInput
            id={`classe-plano-valor-${index}`}
            label={index === 0 ? "Valor (R$)" : ""}
            value={formatarValorMonetario(classePlano.valor)}
          />
        </Col>
        {possuiClassePlanoComContaProvisionamento && (
          <Col className="mt-3" sm={4} md={4} lg={4} xl={4} key={`classe-plano-conta-provisionamento-${index}`}>
            <VisualizacaoInput
              id={`classe-plano-conta-provisionamento-${index}`}
              label={index === 0 ? "Conta de provisionamento" : ""}
              value={classePlano.nomeContaProvisionamento || "-"}
            />
          </Col>
        )}
      </Row>
    );
  };

  const closeModal = () => {
    setVerMaisClasses(false);
    props.closeModal();
  };
  return (
    <CustomModal
      titulo="Visualizar conta a receber"
      onCloseModal={() => closeModal()}
      showModal={props.showModal}
      size="xl"
      tipoModal="MODAL_LATERAL"
      fecharModalAoClicarFora
      scrollable
    >
      {renderBodyModalVisualizarCobranca()}
    </CustomModal>
  );
};
export default ModalVisualizarContaReceber;
